import React, { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import io from 'socket.io-client'
import toast from 'react-hot-toast'

// Components
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import LoadingSpinner from './components/LoadingSpinner'
import LoginForm from './components/LoginForm'
import UpdateNotification from './components/UpdateNotification'

// Contexts
import { AuthProvider, useAuth } from './contexts/AuthContext'

// Pages
import Dashboard from './pages/Dashboard'
import ServicesConfig from './pages/ServicesConfig'
import WidgetsConfig from './pages/WidgetsConfig'
import BookmarksConfig from './pages/BookmarksConfig'
import SettingsConfig from './pages/SettingsConfig'
import ConfigTemplates from './pages/ConfigTemplates'
import Backups from './pages/Backups'
import OfficialDocs from './pages/OfficialDocs'
import CustomStyles from './pages/CustomStyles'
import Changelog from './pages/Changelog'

// API
import { getConfig } from './api/config'

// 受保护的应用组件
function ProtectedApp() {
  const [socket, setSocket] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { isAuthenticated, isPasswordProtected, isLoading, login } = useAuth()

  // 获取配置数据 - 必须在所有条件返回之前调用
  const { data: configResponse, isLoading: configLoading, error, refetch } = useQuery({
    queryKey: ['config'],
    queryFn: getConfig,
    staleTime: 30000, // 30 seconds
    enabled: !isLoading && (isAuthenticated || !isPasswordProtected), // 只有在认证后才获取配置
    retry: 1, // 减少重试次数
  })

  const config = configResponse?.data

  // Socket.IO 连接 - 必须在所有条件返回之前调用
  useEffect(() => {
    // 只有在认证通过或无需认证时才建立Socket连接
    if (isLoading || (isPasswordProtected && !isAuthenticated)) {
      return
    }
    // 动态获取当前访问的服务器地址
    const getSocketUrl = () => {
      // 始终使用当前页面的地址，确保不会跨域
      const { protocol, hostname, port } = window.location

      // 构建Socket.IO连接URL
      let socketUrl = `${protocol}//${hostname}`

      // 添加端口号（只有在端口存在且不是默认端口时）
      const portNum = port ? parseInt(port, 10) : null
      const isDefaultPort = (protocol === 'http:' && portNum === 80) ||
        (protocol === 'https:' && portNum === 443)

      if (portNum && !isDefaultPort) {
        socketUrl += `:${portNum}`
      }

      console.log('当前页面地址:', window.location.href)
      console.log('Socket.IO location:', { protocol, hostname, port, portNum })
      console.log('构建的Socket.IO地址:', socketUrl)

      return socketUrl
    }

    const socketUrl = getSocketUrl()
    console.log('连接到Socket.IO服务器:', socketUrl)

    // 修复：添加Socket.IO连接配置选项
    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'], // 允许降级到轮询
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      timeout: 20000,
      withCredentials: true, // 确保携带cookie
      autoConnect: true
    })
    setSocket(newSocket)

    newSocket.on('connect', () => {
      console.log('✅ 已连接到服务器:', socketUrl, '| 传输方式:', newSocket.io.engine.transport.name)
    })

    newSocket.on('disconnect', (reason) => {
      console.log('❌ 与服务器断开连接:', socketUrl, '| 原因:', reason)
    })

    newSocket.on('connect_error', (error) => {
      console.error('🔴 Socket连接错误:', error.message)
    })

    newSocket.on('configUpdated', (data) => {
      toast.success(`配置 ${data.type} 已更新`)
      refetch()
    })

    newSocket.on('configChanged', (data) => {
      toast(`配置文件 ${data.type} 已被外部修改`, { icon: 'ℹ️' })
      refetch()
    })

    return () => {
      newSocket.close()
    }
  }, [refetch, isLoading, isPasswordProtected, isAuthenticated])

  // 认证加载中
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // 需要密码保护但未认证
  if (isPasswordProtected && !isAuthenticated) {
    return <LoginForm onLoginSuccess={login} />
  }

  if (configLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-400 mb-4">加载配置失败</h1>
          <p className="text-gray-400 mb-4">{error.message}</p>
          <button
            onClick={() => refetch()}
            className="btn-primary"
          >
            重试
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* 侧边栏 */}
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      {/* 主内容区域 */}
      <div className="lg:pl-64">
        {/* 移动端头部 */}
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* 主要内容 */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={<Dashboard config={config} />}
            />
            <Route
              path="/services"
              element={<ServicesConfig config={config?.services} settings={config?.settings} />}
            />
            <Route
              path="/widgets"
              element={<WidgetsConfig config={config?.widgets} />}
            />
            <Route
              path="/bookmarks"
              element={<BookmarksConfig config={config?.bookmarks} />}
            />
            <Route
              path="/settings"
              element={<SettingsConfig config={config?.settings} />}
            />
            <Route
              path="/custom-styles"
              element={<CustomStyles css={config?.customCss} js={config?.customJs} />}
            />
            <Route path="/templates" element={<ConfigTemplates />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/backups" element={<Backups />} />
            <Route path="/docs" element={<OfficialDocs />} />
          </Routes>
        </main>
      </div>

      {/* 版本更新通知 */}
      <UpdateNotification />
    </div>
  )
}

// 主App组件，包装AuthProvider
function App() {
  return (
    <AuthProvider>
      <ProtectedApp />
    </AuthProvider>
  )
}

export default App
